import type { LabelKind, LabelPrintJob } from "../types";
import { escapeZplField } from "./escape";
import { concatZpl, extractPlaceholders } from "./helpers";
import { renderZplTemplate } from "./renderTemplate";
import {
  ALL_LABEL_SIZES,
  DEFAULT_LABEL_SIZE,
  getLabelSize,
  labelSizeLabel,
  labelSizeOptionLabel,
  labelSizeRecommendedUse,
  SPARE_PART_LABEL_SIZE,
} from "./sizes";
import {
  renderSparePartZpl,
  SPARE_PART_TEMPLATE_SIZES,
  SPARE_PART_PLACEHOLDERS,
} from "./spare-part";
import {
  renderStorageBinZpl,
  STORAGE_BIN_TEMPLATE_SIZES,
  STORAGE_BIN_PLACEHOLDERS,
} from "./storage-bin";
import {
  renderPassportZpl,
  PASSPORT_TEMPLATE_SIZES,
  PASSPORT_PLACEHOLDERS,
} from "./passport";
import type { LabelBuildOptions } from "../types";
import type { CompanyLabelZplTemplates } from "../print/labelZplTemplates";
import { resolveZplTemplate } from "../print/labelZplTemplates";
import {
  SHIPPED_LABEL_TEMPLATES,
  COMMON_LABEL_TEMPLATE_SIZES,
  getShippedLabelTemplate,
  getShippedZplBody,
  labelTemplateKey,
  placeholdersForKind,
  isLabelTemplateKey,
  type LabelTemplateKey,
  type ShippedLabelTemplate,
} from "./shippedTemplates";

export { escapeZplField };
export { concatZpl, extractPlaceholders };
export { renderZplTemplate };
export {
  ALL_LABEL_SIZES,
  DEFAULT_LABEL_SIZE,
  getLabelSize,
  labelSizeLabel,
  labelSizeOptionLabel,
  labelSizeRecommendedUse,
  SPARE_PART_LABEL_SIZE,
};
export {
  renderSparePartZpl,
  SPARE_PART_TEMPLATE_SIZES,
  SPARE_PART_PLACEHOLDERS,
};
export {
  renderStorageBinZpl,
  STORAGE_BIN_TEMPLATE_SIZES,
  STORAGE_BIN_PLACEHOLDERS,
};
export {
  renderPassportZpl,
  PASSPORT_TEMPLATE_SIZES,
  PASSPORT_PLACEHOLDERS,
};
export {
  SHIPPED_LABEL_TEMPLATES,
  COMMON_LABEL_TEMPLATE_SIZES,
  getShippedLabelTemplate,
  getShippedZplBody,
  labelTemplateKey,
  placeholdersForKind,
  isLabelTemplateKey,
};
export type { LabelTemplateKey, ShippedLabelTemplate };

/** @deprecated Prefer `renderSparePartZpl`. */
export function buildSparePartZpl(
  data: Parameters<typeof renderSparePartZpl>[0],
  options: LabelBuildOptions = {},
): string {
  return renderSparePartZpl(data, options.size ?? DEFAULT_LABEL_SIZE, options);
}

/** @deprecated Prefer `renderStorageBinZpl`. */
export function buildStorageBinZpl(
  data: Parameters<typeof renderStorageBinZpl>[0],
  options: LabelBuildOptions = {},
): string {
  return renderStorageBinZpl(data, options.size ?? DEFAULT_LABEL_SIZE, options);
}

/** @deprecated Prefer `renderPassportZpl`. */
export function buildPassportZpl(
  data: Parameters<typeof renderPassportZpl>[0],
  options: LabelBuildOptions = {},
): string {
  return renderPassportZpl(data, options.size ?? DEFAULT_LABEL_SIZE, options);
}

type ZplBuilder<K extends LabelKind> = (
  job: LabelPrintJob<K>,
  options: LabelBuildOptions,
) => string;

const BUILDERS: { [K in LabelKind]: ZplBuilder<K> } = {
  "spare-part": (job, options) =>
    renderSparePartZpl(job.data, options.size ?? DEFAULT_LABEL_SIZE, options),
  "storage-bin": (job, options) =>
    renderStorageBinZpl(job.data, options.size ?? DEFAULT_LABEL_SIZE, options),
  passport: (job, options) => renderPassportZpl(job.data, options.size ?? DEFAULT_LABEL_SIZE, options),
};

function resolveJobOptions<K extends LabelKind>(
  job: LabelPrintJob<K>,
  overrides?: CompanyLabelZplTemplates | null,
): LabelBuildOptions {
  const size = job.options?.size ?? DEFAULT_LABEL_SIZE;
  const zplTemplate =
    job.options?.zplTemplate ?? resolveZplTemplate(job.kind, size, overrides);
  return { ...job.options, size, zplTemplate };
}

/** Build ZPL for a single label job. Optional company overrides replace shipped templates. */
export function getZplSizeFallback(
  job: LabelPrintJob,
  overrides?: CompanyLabelZplTemplates | null,
): string | undefined {
  const size = job.options?.size ?? DEFAULT_LABEL_SIZE;
  const override = overrides?.[labelTemplateKey(job.kind, size)];
  if (job.options?.zplTemplate != null || override?.trim() || getShippedLabelTemplate(job.kind, size)) {
    return undefined;
  }
  return size;
}

export function buildZpl<K extends LabelKind>(
  job: LabelPrintJob<K>,
  overrides?: CompanyLabelZplTemplates | null,
): string {
  const options = resolveJobOptions(job, overrides);
  return BUILDERS[job.kind](job, options);
}

/** Build ZPL for multiple labels (concatenated jobs). */
export function buildZplBatch(
  jobs: LabelPrintJob[],
  overrides?: CompanyLabelZplTemplates | null,
): string {
  return concatZpl(jobs.map((job) => buildZpl(job, overrides)));
}
